import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/common/SEO';
import LeadForm from '../../components/LeadForm';
import { trackEvent } from '../../lib/analytics';



const costAreas = [
  { title: 'Cloud Spend', desc: 'Find idle and oversized resources, schedule non-production down, apportion shared and untagged cost to the teams that caused it, and size reservations against what you actually use.', link: '', badge: 'AWS · Azure · GCP · OCI' },
  { title: 'Financial Reconciliations', desc: 'Reconcile across ledgers and close the month without the manual spreadsheet work in between.', link: '/agents/cpa', badge: 'Xero · NetSuite · Sage' },
  { title: 'IT Service Desk', desc: 'Triage incidents and service requests, pull the history behind a ticket, and route the repetitive ones without a person reading each queue.', link: '', badge: 'ServiceNow' },
  { title: 'Sales Operations', desc: 'Surface stale opportunities, single-threaded deals, gap to quota and customers with no open activity — the pipeline hygiene nobody has time to run weekly.', link: '', badge: 'Salesforce' },
  { title: 'Document Processing', desc: 'Read, classify and process documents at volume, so nobody is re-typing what a PDF already says.', link: '/agents/doc-parsing', badge: 'Invoices · POs · Claims' },
  { title: 'Cloud Architecture Reviews', desc: 'Cut the time it takes to produce a reviewed architecture design, from days of drafting to a few hours.', link: '/agents/cloud-architect', badge: 'Design · Diagram · IaC' },
  { title: 'Data Pipeline Builds', desc: 'Cut pipeline build time by taking the boilerplate, schema design and testing off your engineers.', link: '/agents/data-engineering', badge: 'Schema · Tests' },
  { title: 'Recruitment Screening', desc: 'Screen hundreds of applicants and handle the scheduling, so recruiters start at the shortlist.', link: '/agents/interview-agent', badge: 'Screen · Schedule' },
  { title: 'Sales Prospecting', desc: 'Automate the prospecting research and first-draft outreach, so your reps spend their time on conversations.', link: '/agents/sdr-agent', badge: 'Research · Outreach' },
];

const steps = [
  { num: '01', title: 'Identify the Bottleneck Workflow', desc: 'Map your highest-volume, most repetitive manual workflows — the ones consuming the most team hours per week.' },
  { num: '02', title: 'Put It Live With Guardrails', desc: 'Connect it to your data and systems, with approval gates so a person signs off before anything changes.' },
  { num: '03', title: 'Measure Against the Baseline', desc: 'Track time saved per workflow, cost per output and error rate against the manual baseline you recorded before go-live, over the first 30 days.' },
];

const faqs = [
  {
    q: 'How much can we realistically take out of operating cost?',
    a: 'Teams typically see 40–70% less time spent on the workflows they automate. The size of the win depends on volume and repeatability: a review process that runs a hundred times a month moves the needle far more than one that runs twice.',
  },
  {
    q: 'Which parts of the business benefit most?',
    a: 'Finance and accounting, cloud infrastructure, data engineering, recruitment screening and sales prospecting — the functions with high, repetitive volume and a clear right answer. Work that is different every time benefits least, and we will say so.',
  },
  {
    q: 'How long before we see a return?',
    a: 'Most organisations can measure the difference within 14 to 30 days of going live, because the baseline is simply the hours the work took before. We agree what gets measured up front so the comparison is not an argument later.',
  },
  {
    q: 'Does this replace our people?',
    a: 'No. It takes the repetitive, high-volume work so your experts spend their time on decisions, exceptions and the things that actually need judgement. Nothing changes without a person approving it.',
  },
  {
    q: 'Can this run inside our own cloud?',
    a: 'Yes. It can run privately inside your own AWS, Azure or GCP environment, with no external telemetry and nothing retained outside it.',
  },
];

const OpsROICalculator = () => {
  const [teamSize, setTeamSize] = useState(15);
  const [hourlyRate, setHourlyRate] = useState(65);

  const annualHoursPerEmp = 20 * 48; // 20 hours manual repetitive work/week
  const totalManualCost = Math.round(teamSize * annualHoursPerEmp * hourlyRate);
  const projectedSavings = Math.round(totalManualCost * 0.65); // 65% cost savings

  return (
    <div style={{ 
      background: 'white', 
      borderRadius: '24px', 
      padding: '40px', 
      boxShadow: '0 20px 40px rgba(0,0,0,0.08)', 
      border: '1px solid #e2e8f0', 
      maxWidth: '850px', 
      margin: '40px auto 0' 
    }}>
      <h3 style={{ fontSize: '1.8rem', fontWeight: 800, marginBottom: '8px', textAlign: 'center', color: '#0f172a', letterSpacing: '-0.02em' }}>
        Calculate Your Annual Operational Cost Savings
      </h3>
      <p style={{ color: '#64748b', fontSize: '1rem', textAlign: 'center', marginBottom: '32px' }}>
        See what your organisation saves by taking the repetitive work off your team.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '32px', alignItems: 'center' }}>
        <div>
          <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px', color: '#475569' }}>
            Team Size (Operations / Tech / Finance)
          </label>
          <input 
            type="range" 
            min="5" 
            max="150" 
            value={teamSize} 
            onChange={e => setTeamSize(Number(e.target.value))} 
            style={{ width: '100%', marginBottom: '12px', accentColor: '#0071e3' }} 
          />
          <div style={{ fontWeight: 800, fontSize: '1.2rem', color: '#0071e3', marginBottom: '24px' }}>
            {teamSize} Employees
          </div>

          <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px', color: '#475569' }}>
            Avg Hourly Loaded Rate ($)
          </label>
          <input 
            type="range" 
            min="35" 
            max="180" 
            value={hourlyRate} 
            onChange={e => setHourlyRate(Number(e.target.value))} 
            style={{ width: '100%', marginBottom: '12px', accentColor: '#0071e3' }} 
          />
          <div style={{ fontWeight: 800, fontSize: '1.2rem', color: '#0071e3' }}>
            ${hourlyRate}/hr
          </div>
        </div>

        <div style={{ background: '#f8fafc', padding: '28px', borderRadius: '20px', border: '1px solid #cbd5e1' }}>
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Baseline Manual Workflow Cost
            </div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#ef4444', marginTop: '4px' }}>
              ${totalManualCost.toLocaleString()} / yr
            </div>
          </div>
          
          <div style={{ height: '1px', background: '#cbd5e1', margin: '16px 0' }} />

          <div>
            <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Projected Savings
            </div>
            <div style={{ fontSize: '2.2rem', fontWeight: 900, color: '#10b981', marginTop: '4px' }}>
              ${projectedSavings.toLocaleString()} / yr
            </div>
            <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '8px', lineHeight: 1.5 }}>
*Illustrative: assumes 20 hours of repetitive work per person per week and a 65% reduction on that portion. Your figure depends on how repeatable the work is — the assessment establishes it against your own baseline.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function OpsFAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (i) => {
    setOpenIndex(openIndex === i ? null : i);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '850px', margin: '0 auto' }}>
      {faqs.map((faq, i) => {
        const isOpen = openIndex === i;
        return (
          <div 
            key={i} 
            style={{ 
              borderRadius: '16px', 
              border: isOpen ? '2px solid #0071e3' : '1px solid rgba(148,163,184,0.2)', 
              background: 'white', 
              overflow: 'hidden',
              transition: 'all 0.2s ease',
              boxShadow: isOpen ? '0 10px 25px rgba(0, 113, 227, 0.08)' : '0 2px 8px rgba(0,0,0,0.02)'
            }}
          >
            <button
              onClick={() => toggle(i)}
              style={{
                width: '100%',
                padding: '22px 28px',
                background: 'none',
                border: 'none',
                textAlign: 'left',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                fontSize: '1.1rem',
                fontWeight: 700,
                color: '#0f172a'
              }}
            >
              <span>{faq.q}</span>
              <span style={{ fontSize: '1.4rem', color: '#0071e3', fontWeight: 800, transition: 'transform 0.2s', transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)' }}>
                +
              </span>
            </button>

            {isOpen && (
              <div style={{ padding: '0 28px 24px 28px', color: '#475569', fontSize: '1rem', lineHeight: 1.7, borderTop: '1px solid #f1f5f9' }}>
                <p style={{ margin: '16px 0 0 0' }}>{faq.a}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function ReduceOperationalCosts() {
  const sectionLight = { padding: '80px 0', background: '#f8fafc' };
  const sectionWhite = { padding: '80px 0', background: 'white' };
  const sectionDark = { padding: '80px 0', background: '#0f172a' };
  const container = { maxWidth: '1100px', margin: '0 auto', padding: '0 24px' };
  const card = { borderRadius: '20px', border: '1px solid rgba(148,163,184,0.16)', background: 'white', padding: '28px 32px' };

  return (
    <>
      <SEO
        title="Reduce Operational Costs | 40-70% Cost Savings | Eagentix"
        description="Cut operational cost by replacing repetitive manual workflows with automation across cloud spend, finance, service desk and sales operations — measured against your own baseline."
        pathname="/use-cases/reduce-operational-costs"
      />

      {/* Keyframe animation for floating 3D graphics */}
      <style>{`
        @keyframes float3D {
          0% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(0.5deg); }
          100% { transform: translateY(0px) rotate(0deg); }
        }
        .floating-hero-3d {
          animation: float3D 6s ease-in-out infinite;
        }
        @media (min-width: 960px) {
          .hero-split-grid {
            grid-template-columns: 1.1fr 0.9fr !important;
          }
        }
        @media (max-width: 768px) {
          .hero-h1 { font-size: 2.1rem !important; line-height: 1.25 !important; }
          .hero-section { padding-top: 80px !important; padding-bottom: 60px !important; }
        }
      `}</style>

      {/* Hero */}
      <section className="hero-section" style={{ 
        paddingTop: '120px', 
        paddingBottom: '140px',
        background: '#020617',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Video (Visible Opacity & Correct zIndex Stack) */}
        <video 
          autoPlay 
          loop 
          playsInline 
          muted 
          style={{ 
            position: 'absolute', 
            top: '50%', 
            left: '50%', 
            width: '100%', 
            height: '100%', 
            objectFit: 'cover', 
            transform: 'translate(-50%, -50%)', 
            zIndex: 1, 
            opacity: 0.65 
          }}
        >
          <source src="/videos/operations-bg.mp4" type="video/mp4" />
        </video>

        {/* Semi-transparent Overlay for Text Legibility */}
        <div style={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'linear-gradient(to right, rgba(2, 6, 23, 0.92) 0%, rgba(15, 23, 42, 0.75) 50%, rgba(2, 6, 23, 0.88) 100%)', 
          zIndex: 2 
        }} />

        {/* Ambient Glow */}
        <div style={{ position: 'absolute', top: '20%', right: '10%', width: '500px', height: '500px', background: 'radial-gradient(circle, rgba(56, 189, 248, 0.25) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 2 }} />

        <div style={{ ...container, position: 'relative', zIndex: 3, maxWidth: '1280px' }}>
          <div className="hero-split-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '48px', alignItems: 'center' }}>
            <div style={{ maxWidth: '680px' }}>
              <span style={{ color: '#38bdf8', fontWeight: 800, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', marginBottom: '16px' }}>
                Use Case · Operational Efficiency & Cost Reduction
              </span>
              <h1 className="hero-h1" style={{ fontSize: '3.2rem', fontWeight: 900, color: 'white', letterSpacing: '-0.03em', margin: '0 0 24px', lineHeight: 1.15 }}>
                Cut Operational Costs by <span style={{ color: '#38bdf8' }}>40% to 70%</span>
              </h1>
              <p style={{ fontSize: '1.2rem', color: '#cbd5e1', lineHeight: 1.75, marginBottom: '36px', fontWeight: 500 }}>
                Enterprise teams reduce operational overhead by replacing manual, repetitive workflows with purpose-built automation that works 24/7, never fatigue, and produce audit-ready outputs.
              </p>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <a
                  href="#lead-form"
                  onClick={() => trackEvent('cta_click', { button: 'Hero Calculate Cost Reduction' })}
                  style={{ background: 'linear-gradient(135deg, #0071e3, #2563eb)', color: 'white', padding: '16px 36px', borderRadius: '12px', fontWeight: 900, textDecoration: 'none', fontSize: '1.1rem', boxShadow: '0 12px 30px rgba(37,99,235,0.4)' }}
                >
                  Calculate Your Cost Reduction →
                </a>
                <a
                  href="#areas"
                  style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '16px 32px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1.1rem', border: '1px solid rgba(255,255,255,0.2)' }}
                >
                  View Cost Areas
                </a>
              </div>

              {/* Social Proof Micro-strip */}
              <div style={{ marginTop: '28px', color: '#94a3b8', fontSize: '0.9rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ color: '#38bdf8' }}>✓ Measured against your own baseline</span>
                <span>•</span>
                <span>Zero Data Retention</span>
              </div>
            </div>

            {/* 3D Animated Hero Card (Dribbble 3D Floating Glass) */}
            <div className="floating-hero-3d" style={{ position: 'relative', width: '100%', maxWidth: '560px', justifySelf: 'center' }}>
              <div style={{ borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.2)', boxShadow: '0 30px 70px -15px rgba(56, 189, 248, 0.4)', background: 'rgba(15, 23, 42, 0.9)' }}>
                <img
                  src="/images/operations-cost-dashboard.webp"
                  alt="An operations team reviewing a cost reduction dashboard showing illustrative savings, cycle time and spend-by-category figures"
                  width="2096"
                  height="1184"
                  loading="eager"
                  decoding="async"
                  style={{ width: '100%', height: 'auto', display: 'block', aspectRatio: '2096 / 1184' }}
                />
              </div>

              {/* Dribbble-style Floating Glass Pill - Bottom Left */}
              <div style={{
                position: 'absolute',
                bottom: '16px',
                left: '16px',
                background: 'rgba(15, 23, 42, 0.92)',
                backdropFilter: 'blur(16px)',
                border: '1px solid rgba(56, 189, 248, 0.5)',
                padding: '10px 18px',
                borderRadius: '16px',
                color: 'white',
                boxShadow: '0 10px 30px rgba(0,0,0,0.6)',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                zIndex: 5
              }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#38bdf8', boxShadow: '0 0 10px #38bdf8' }} />
                <div>
                  <div style={{ fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 800 }}>Typical Target Range</div>
                  <div style={{ fontSize: '0.95rem', fontWeight: 900, color: '#38bdf8' }}>40–70% Overhead</div>
                </div>
              </div>

              <p style={{ margin: '12px 4px 0', color: '#64748b', fontSize: '0.78rem', lineHeight: 1.5, textAlign: 'center' }}>
                Illustrative dashboard. Figures shown are examples, not a specific client result.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Above the fold proof strip */}
      <section style={{ background: '#090d16', padding: '40px 0 64px 0', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={container}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
            <div style={{ background: 'rgba(15, 23, 42, 0.9)', padding: '24px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)' }}>
              <div style={{ fontSize: '1.8rem', fontWeight: 900, color: '#38bdf8', marginBottom: '4px' }}>$420k+</div>
              <div style={{ color: '#cbd5e1', fontSize: '0.9rem', fontWeight: 600 }}>Average Annual Cost Savings per Enterprise Deployment</div>
            </div>

            <div style={{ background: 'rgba(15, 23, 42, 0.9)', padding: '24px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)' }}>
              <div style={{ fontSize: '1.8rem', fontWeight: 900, color: '#34d399', marginBottom: '4px' }}>68% Cut</div>
              <div style={{ color: '#cbd5e1', fontSize: '0.9rem', fontWeight: 600 }}>Reduction in Cycle Time for High-Volume Workflows</div>
            </div>

            <div style={{ background: 'rgba(15, 23, 42, 0.9)', padding: '24px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)' }}>
              <div style={{ fontSize: '1.8rem', fontWeight: 900, color: '#c084fc', marginBottom: '4px' }}>100% Audit-Ready</div>
              <div style={{ color: '#cbd5e1', fontSize: '0.9rem', fontWeight: 600 }}>Traceable Policy-as-Code Execution Logs</div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Savings Calculator */}
      <section style={{ ...sectionWhite, padding: '80px 0' }}>
        <div style={container}>
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <span style={{ color: '#0071e3', fontWeight: 800, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              Interactive ROI Tool
            </span>
          </div>
          <OpsROICalculator />
        </div>
      </section>

      {/* Where AI reduces costs */}
      <section style={sectionLight} id="areas">
        <div style={container}>
          <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px', textAlign: 'center' }}>
            Where AI Reduces Costs Most
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '48px', maxWidth: '680px', margin: '0 auto 48px', textAlign: 'center' }}>
            The the operational functions where automation delivers the highest cost reduction per deployment.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
            {costAreas.map((area) => {
              const inner = (
                <>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
                    <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', margin: 0, minWidth: 0 }}>{area.title}</h3>
                    <span style={{ flex: '0 0 auto', background: '#dbeafe', color: '#1d4ed8', fontSize: '0.72rem', fontWeight: 800, padding: '4px 10px', borderRadius: '100px', whiteSpace: 'nowrap' }}>
                      {area.badge}
                    </span>
                  </div>
                  <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.65, margin: 0 }}>{area.desc}</p>
                  {area.link && (
                    <div style={{ marginTop: '16px', color: '#0071e3', fontWeight: 700, fontSize: '0.9rem' }}>
                      See how it works →
                    </div>
                  )}
                </>
              );
              const base = { ...card, display: 'block', position: 'relative' };
              return area.link ? (
                <Link key={area.title} to={area.link} style={{ ...base, textDecoration: 'none', transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}>
                  {inner}
                </Link>
              ) : (
                <div key={area.title} style={base}>{inner}</div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How the cost comes out */}
      <section style={sectionWhite}>
        <div style={container}>
          <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px', textAlign: 'center' }}>
            How the Cost Comes Out
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '48px', maxWidth: '680px', margin: '0 auto 48px', textAlign: 'center' }}>
            A proven 3-step deployment model from workflow discovery to measured monthly savings.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '28px' }}>
            {steps.map((step, i) => (
              <div key={i} style={{ ...card, position: 'relative' }}>
                <div style={{ fontSize: '2.2rem', fontWeight: 900, color: '#0071e3', marginBottom: '12px', fontVariantNumeric: 'tabular-nums' }}>{step.num}</div>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0f172a', marginBottom: '12px' }}>{step.title}</h3>
                <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For vs NOT For (Qualification Filter §3.9) */}
      <section style={{ ...sectionLight, padding: '80px 0' }}>
        <div style={container}>
          <div style={{ textAlign: 'center', marginBottom: '48px', maxWidth: '800px', margin: '0 auto' }}>
            <span style={{ color: '#0ea5e9', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', marginBottom: '12px' }}>
              Qualification Filter
            </span>
            <h2 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em' }}>
              Is Your Organization Ready for AI Cost Reduction?
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '32px', maxWidth: '950px', margin: '0 auto' }}>
            {/* IS FOR */}
            <div style={{ background: 'white', borderRadius: '24px', padding: '36px', border: '2px solid #10b981', boxShadow: '0 10px 30px rgba(16, 185, 129, 0.08)' }}>
              <div style={{ color: '#10b981', fontWeight: 800, fontSize: '1.3rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</div>
                Who This IS For
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '16px', color: '#334155', fontSize: '1rem', lineHeight: 1.6 }}>
                <li style={{ paddingLeft: '24px', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#10b981', fontWeight: 800 }}>✓</span>
                  <strong>COOs, VP Ops & Engineering Leaders</strong> managing high-volume manual work in cloud, finance, or data pipelines.
                </li>
                <li style={{ paddingLeft: '24px', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#10b981', fontWeight: 800 }}>✓</span>
                  Companies looking for <strong>measurable cost reduction metrics</strong> within 30 days of deployment.
                </li>
                <li style={{ paddingLeft: '24px', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#10b981', fontWeight: 800 }}>✓</span>
                  Organizations requiring <strong>private cloud (VPC) deployment</strong> and zero data retention.
                </li>
              </ul>
            </div>

            {/* IS NOT FOR */}
            <div style={{ background: 'white', borderRadius: '24px', padding: '36px', border: '1px solid #cbd5e1', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
              <div style={{ color: '#ef4444', fontWeight: 800, fontSize: '1.3rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</div>
                Who This Is NOT For
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '16px', color: '#64748b', fontSize: '1rem', lineHeight: 1.6 }}>
                <li style={{ paddingLeft: '24px', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#ef4444', fontWeight: 800 }}>✕</span>
                  Individual freelancers seeking simple personal productivity scripts.
                </li>
                <li style={{ paddingLeft: '24px', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#ef4444', fontWeight: 800 }}>✕</span>
                  Teams expecting fully unsupervised autonomous systems without governance checkpoints.
                </li>
                <li style={{ paddingLeft: '24px', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#ef4444', fontWeight: 800 }}>✕</span>
                  Companies unwilling to benchmark baseline manual execution hours.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How we work — agreed target, not a promised number */}
      <section style={{ background: sectionDark.background, padding: '80px 0', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={{ ...container, maxWidth: '850px', textAlign: 'center' }}>
          <div style={{ 
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', 
            borderRadius: '24px', 
            padding: '48px', 
            border: '2px solid #38bdf8', 
            boxShadow: '0 25px 50px -12px rgba(56, 189, 248, 0.3)' 
          }}>
            <span style={{ color: '#38bdf8', fontWeight: 800, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', marginBottom: '12px' }}>
              How We Work
            </span>
            <h3 style={{ fontSize: '2.2rem', fontWeight: 900, color: 'white', letterSpacing: '-0.02em', marginBottom: '16px' }}>
We agree the target before you pay for it.
            </h3>
            <p style={{ color: '#cbd5e1', fontSize: '1.1rem', lineHeight: 1.7, maxWidth: '700px', margin: '0 auto 28px' }}>
Before onboarding starts we write down the workflow, the baseline it runs at today, and the improvement we both consider a success — measured against your own numbers, not a figure promised before we have seen your process. You know what we are aiming at, and how it will be checked, before you commit.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={sectionLight}>
        <div style={container}>
          <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '40px', textAlign: 'center' }}>
            Frequently Asked Questions
          </h2>
          <OpsFAQ />
        </div>
      </section>

      {/* Cross-link: free AI Process Assessment */}
      <section style={{ padding: '64px 0', background: 'white' }}>
        <div style={container}>
          <div style={{ display: 'flex', gap: '32px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between', padding: '36px 40px', borderRadius: '20px', border: '1px solid #dbe4f0', background: '#f5f8fc' }}>
            <div style={{ flex: '1 1 420px' }}>
              <span style={{ display: 'block', marginBottom: '10px', color: '#2563eb', fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
                Free 3-hour working session
              </span>
              <h2 style={{ fontSize: '1.8rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', margin: '0 0 10px', lineHeight: 1.25 }}>
                Start by getting one job off your plate for good.
              </h2>
              <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.65, margin: 0, maxWidth: '620px' }}>
                Three hours with your team. We find the three jobs eating the most time, build the first one running on your own tools, and map the other two. You keep whatever we build.
              </p>
            </div>
            <Link
              to="/ai-process-assessment"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '17px 30px', borderRadius: '12px', background: '#2563eb', color: 'white', fontWeight: 800, fontSize: '1.05rem', textDecoration: 'none', boxShadow: '0 14px 32px rgba(37, 99, 235, 0.3)', whiteSpace: 'nowrap' }}
            >
              Check fit and book →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA / Contact Section with P.S. Block (§3.12) */}
      <section id="lead-form" style={{ padding: '80px 0', background: '#020617' }}>
        <div style={{ ...container, maxWidth: '700px', textAlign: 'center' }}>
          <span style={{ color: '#38bdf8', fontWeight: 800, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', marginBottom: '12px' }}>
            By Application
          </span>
          <h2 style={{ fontSize: '2.5rem', fontWeight: 900, color: 'white', letterSpacing: '-0.03em', marginBottom: '16px' }}>
            Ready to Cut Operational Overhead?
          </h2>
          <p style={{ color: '#94a3b8', fontSize: '1.1rem', lineHeight: 1.7, marginBottom: '36px' }}>
            Every scoping audit is run against your own architecture and cost data, so we qualify each one before we book it.
          </p>
          
          <div style={{ textAlign: 'left' }}>
            <LeadForm
              page="/use-cases/reduce-operational-costs"
              showTeamSize
              teamSizeLabel="Operations team size"
              title="Book a cost reduction scoping session"
              subtitle="Three required fields. We reply within one business day."
              buttonLabel="Book a Scoping Session"
              messageLabel="Which workflow costs you the most today? (optional)"
              messagePlaceholder="e.g. Four people spend most of the month reconciling ledgers by hand..."
              successText="Thanks — we'll respond within one business day to scope a cost reduction session for your team."
            />
          </div>

          <p style={{ margin: '20px 0 0', color: '#64748b', fontSize: '0.92rem', lineHeight: 1.6 }}>
            Prefer email? <a href="mailto:contact@eagentix.com" onClick={() => trackEvent('cta_click', { button: 'Ops Email Fallback' })} style={{ color: '#93c5fd' }}>contact@eagentix.com</a>
          </p>

          {/* P.S. Section (§3.12) */}
          <div style={{ marginTop: '40px', padding: '24px', borderRadius: '16px', background: 'rgba(30, 41, 59, 0.8)', border: '1px dashed #38bdf8', textAlign: 'center' }}>
            <p style={{ color: '#94a3b8', fontSize: '0.95rem', lineHeight: 1.6, margin: 0, fontStyle: 'italic' }}>
              <strong style={{ color: '#38bdf8', fontStyle: 'normal' }}>P.S.</strong> All cost-saving integrations operate with zero vendor lock-in and can be deployed inside your own cloud infrastructure (AWS, Azure, GCP).
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
